// TypeScript mirror of the shell protocol spoken by the shared Swift core
// (KSE-0185 phase 3). Field names come from the Swift models — do not invent
// or rename anything here without changing the Swift side first:
//
//   MailShellSnapshot            Apple/Lately/Sources/PersistenceCore/MailShellController.swift
//   StoredAccount                Apple/Lately/Sources/PersistenceCore/StatePersistenceController+JMAPCache.swift
//   PushRegistration             Apple/Lately/Sources/PersistenceCore/StatePersistenceController+Push.swift
//   PushSubscriptionKeys,
//   PushRegistrationResult       Apple/Lately/Sources/JMAPKit/Models.swift,
//                                Apple/Lately/Sources/PersistenceCore/PushRegistrationController.swift
//   ThreadGroup                  Apple/Lately/Sources/MailCore/Threading.swift
//   Mailbox, MailboxRights, EmailMessage, EmailAddress, EmailBodyPart,
//   EmailBodyValue, Attachment, EmailHeader, JMAPSession, JMAPAccount,
//   JMAPClientSnapshot, JMAPSecretKind, OutgoingEmailDraft,
//   EmailSubmissionResult        Apple/Lately/Sources/JMAPKit/Models.swift
//
// Encoding: JSONEncoder with .iso8601 dates. Swift optionals are emitted with
// encodeIfPresent, so a nil property is an ABSENT key (never `null`); the
// types below use `?:` for those and additionally tolerate `null` so a
// hand-written fixture cannot break the renderer. Swift `URL` encodes as a
// plain string.

export type Maybe<T> = T | null | undefined;

// ---------------------------------------------------------------------------
// JMAPKit models

export type JMAPSecretKind = "basicPassword" | "bearerToken" | "oidcSession";

export interface JMAPAccount {
  name?: Maybe<string>;
  isPersonal?: Maybe<boolean>;
  accountCapabilities?: Maybe<Record<string, unknown>>;
}

export interface JMAPSession {
  capabilities: Record<string, unknown>;
  accounts: Record<string, JMAPAccount>;
  primaryAccounts: Record<string, string>;
  apiUrl: string;
  downloadUrl: string;
  uploadUrl?: Maybe<string>;
  eventSourceUrl?: Maybe<string>;
}

export interface JMAPClientSnapshot {
  serverURL: string;
  session: JMAPSession;
  mailAccountId?: Maybe<string>;
  observedStates: Record<string, string>;
  supportsContacts: boolean;
  supportsCalendars: boolean;
}

export interface MailboxRights {
  mayReadItems: boolean;
  mayAddItems: boolean;
  mayRemoveItems: boolean;
  maySetSeen: boolean;
  maySetKeywords: boolean;
  mayCreateChild: boolean;
  mayRename: boolean;
  mayDelete: boolean;
  maySubmit: boolean;
}

export interface Mailbox {
  id: string;
  // Server-side id when `id` has been namespaced for a shared account.
  originalId?: Maybe<string>;
  name: string;
  parentId?: Maybe<string>;
  role?: Maybe<string>;
  sortOrder: number;
  totalEmails: number;
  unreadEmails: number;
  totalThreads: number;
  unreadThreads: number;
  myRights: MailboxRights;
  isSubscribed: boolean;
  accountId?: Maybe<string>;
  accountName?: Maybe<string>;
  isShared: boolean;
}

export interface EmailAddress {
  name?: Maybe<string>;
  email: string;
}

export interface EmailBodyValue {
  value: string;
  isEncodingProblem?: Maybe<boolean>;
  isTruncated?: Maybe<boolean>;
}

export interface EmailBodyPart {
  partId: string;
  blobId: string;
  size: number;
  name?: Maybe<string>;
  type: string;
  charset?: Maybe<string>;
  disposition?: Maybe<string>;
  cid?: Maybe<string>;
  language?: Maybe<string[]>;
  location?: Maybe<string>;
  subParts?: Maybe<EmailBodyPart[]>;
}

export interface Attachment {
  partId: string;
  blobId: string;
  size: number;
  name?: Maybe<string>;
  type: string;
  charset?: Maybe<string>;
  cid?: Maybe<string>;
  disposition?: Maybe<string>;
}

export interface EmailHeader {
  name: string;
  value: string;
}

export interface EmailMessage {
  id: string;
  threadId: string;
  mailboxIds: Record<string, boolean>;
  keywords: Record<string, boolean>;
  size: number;
  // RFC 3339 / ISO 8601 date strings, exactly as JMAP delivers them.
  receivedAt: string;
  from?: Maybe<EmailAddress[]>;
  to?: Maybe<EmailAddress[]>;
  cc?: Maybe<EmailAddress[]>;
  bcc?: Maybe<EmailAddress[]>;
  replyTo?: Maybe<EmailAddress[]>;
  subject?: Maybe<string>;
  sentAt?: Maybe<string>;
  preview?: Maybe<string>;
  textBody?: Maybe<EmailBodyPart[]>;
  htmlBody?: Maybe<EmailBodyPart[]>;
  bodyValues?: Maybe<Record<string, EmailBodyValue>>;
  attachments?: Maybe<Attachment[]>;
  hasAttachment: boolean;
  messageId?: Maybe<string[]>;
  inReplyTo?: Maybe<string[]>;
  references?: Maybe<string[]>;
  headers?: Maybe<EmailHeader[]>;
}

export interface OutgoingEmailDraft {
  // Omitted → the controller fills it from the active account's username.
  from?: EmailAddress;
  to: EmailAddress[];
  cc: EmailAddress[];
  bcc: EmailAddress[];
  subject: string;
  body: string;
  inReplyTo: string[];
  references: string[];
}

export interface EmailSubmissionResult {
  emailId: string;
  submissionId: string;
}

// ---------------------------------------------------------------------------
// MailCore

export interface ThreadGroup {
  threadId: string;
  // Newest first.
  emails: EmailMessage[];
  latestEmail: EmailMessage;
  participantNames: string[];
  hasUnread: boolean;
  hasStarred: boolean;
  hasAttachment: boolean;
  emailCount: number;
}

// ---------------------------------------------------------------------------
// PersistenceCore

export interface StoredAccount {
  storageKey: string;
  serverURL: string;
  username: string;
  credentialKind: JMAPSecretKind;
  lastConnectedAt?: Maybe<string>;
  supportsContacts: boolean;
  supportsCalendars: boolean;
  session?: Maybe<JMAPClientSnapshot>;
  isActive: boolean;
}

// RFC 8291 receiver keys as PushSubscription.toJSON() reports them (base64url).
export interface PushSubscriptionKeys {
  p256dh: string;
  auth: string;
}

// A Web Push registration the core holds (PersistenceCore.PushRegistration):
// the JMAP PushSubscription id plus what the server never reports back — the
// endpoint and whether the PushVerification code has been set. `push` on the
// snapshot lists every registration of this install; the UI picks the active
// account's by `accountStorageKey` + `accountId`.
export interface PushRegistration {
  accountId: string;
  // The stored account the row belongs to — the same value as
  // StoredAccount.storageKey / MailShellSnapshot.activeAccountStorageKey.
  // JMAP account ids are short and reused per server, so two accounts on
  // different servers can share `accountId`; this is what tells them apart.
  // Absent only on cores predating the field (then match by accountId alone).
  accountStorageKey?: Maybe<string>;
  id: string;
  endpoint: string;
  deviceClientId: string;
  verified: boolean;
  // JMAP UTCDate when the server reported one.
  expires?: Maybe<string>;
}

// ---------------------------------------------------------------------------
// Notification digest (PersistenceCore.NotificationReconciler, mirrored 1:1;
// dates are ISO 8601 strings across the bridge). One digest is the same
// computation on every platform: what the platform currently displays
// (`delivered`, read back from the notification center / registration) plus
// the persisted cursors in, and {show, clear, badge, cursors} out. Identity of
// a notification everywhere: tag / threadIdentifier
// `lately-thread-<accountStorageKey>-<threadId>` (see notificationTag) and
// `data.lately` = {accountStorageKey, accountId, threadId, emailId}.

export interface NotificationRef {
  accountStorageKey: string;
  accountId: string;
  threadId: string;
  emailId: string;
}

export interface DeliveredNotification extends NotificationRef {
  // The platform identifier: the Web Notification `tag`.
  identifier: string;
}

export interface NotifiedEmail {
  emailId: string;
  threadId: string;
  notifiedAt: string;
}

export interface NotificationCursor {
  emailState?: Maybe<string>;
  inboxMailboxId?: Maybe<string>;
  notified: NotifiedEmail[];
  updatedAt?: Maybe<string>;
}

export interface NotificationToShow extends NotificationRef {
  tag: string;
  title: string;
  body: string;
  receivedAt: string;
}

export type NotificationClearReason = "seen" | "leftInbox" | "destroyed" | "accountRemoved";

export interface NotificationToClear {
  identifier: string;
  accountStorageKey: string;
  threadId: string;
  emailId: string;
  reason: NotificationClearReason;
}

export interface NotificationDigest {
  show: NotificationToShow[];
  clear: NotificationToClear[];
  // Inbox unread across accounts (null when no account answered).
  badge?: Maybe<number>;
  cursors: Record<string, NotificationCursor>;
}

export const NOTIFICATION_TAG_PREFIX = "lately-thread-";

export function notificationTag(accountStorageKey: string, threadId: string): string {
  return `${NOTIFICATION_TAG_PREFIX}${accountStorageKey}-${threadId}`;
}

// ---------------------------------------------------------------------------
// Calendar (KSE-0191): the shared core's CalendarShellController computes
// occurrences + day/week/month/agenda layouts and relays a
// CalendarShellSnapshot through `host_calendar_snapshot`. Mirrors
//   CalendarDate, CalendarDateRange              CalendarCore/CalendarDate.swift
//   CalendarOccurrence, Calendar*Layout*,
//   CalendarShellSnapshot                        CalendarCore/CalendarLayoutModels.swift
//   Calendar (JMAP), CalendarEvent, participants JMAPKit/Models.swift
// The React side only draws these — no date math beyond formatting.

export interface CalendarDate {
  year: number;
  // 1-12
  month: number;
  // 1-31
  day: number;
}

// Inclusive on both ends.
export interface CalendarDateRange {
  start: CalendarDate;
  end: CalendarDate;
}

export type CalendarViewMode = "day" | "week" | "month" | "agenda";

export type CalendarEventStatus = "tentative" | "confirmed" | "cancelled";
export type CalendarFreeBusyStatus = "free" | "busy";
export type CalendarPrivacy = "public" | "private" | "secret";
export type CalendarParticipationStatus =
  | "accepted"
  | "declined"
  | "tentative"
  | "delegated"
  | "needs-action";
export type CalendarParticipantKind = "individual" | "group" | "location" | "resource";

export interface CalendarRights {
  mayReadFreeBusy: boolean;
  mayReadItems: boolean;
  mayWriteAll: boolean;
  mayWriteOwn: boolean;
  mayUpdatePrivate: boolean;
  mayRSVP: boolean;
  mayAdmin: boolean;
  mayShare: boolean;
  mayDelete: boolean;
}

// JMAPKit.Calendar (RFC-draft JMAP Calendars `Calendar` object).
export interface JMAPCalendar {
  id: string;
  name: string;
  description?: Maybe<string>;
  // "#RRGGBB" when the server has one.
  color?: Maybe<string>;
  sortOrder: number;
  isSubscribed: boolean;
  isVisible: boolean;
  isDefault: boolean;
  includeInAvailability: "all" | "attending" | "none";
  timeZone?: Maybe<string>;
  myRights: CalendarRights;
}

export interface CalendarLink {
  "@type"?: Maybe<string>;
  href: string;
  cid?: Maybe<string>;
  contentType?: Maybe<string>;
  size?: Maybe<number>;
  rel?: Maybe<string>;
  display?: Maybe<string>;
  title?: Maybe<string>;
}

export interface CalendarLocation {
  "@type"?: Maybe<string>;
  name: string;
  description?: Maybe<string>;
  locationTypes?: Maybe<Record<string, boolean>>;
  coordinates?: Maybe<string>;
  timeZone?: Maybe<string>;
  links?: Maybe<Record<string, CalendarLink>>;
  relativeTo?: Maybe<string>;
}

export interface CalendarVirtualLocation {
  "@type"?: Maybe<string>;
  name?: Maybe<string>;
  description?: Maybe<string>;
  uri: string;
  features?: Maybe<Record<string, boolean>>;
}

export interface CalendarParticipant {
  "@type"?: Maybe<string>;
  name: string;
  email?: Maybe<string>;
  calendarAddress?: Maybe<string>;
  description?: Maybe<string>;
  sendTo?: Maybe<Record<string, string>>;
  kind: CalendarParticipantKind;
  roles: Record<string, boolean>;
  participationStatus: CalendarParticipationStatus;
  participationComment?: Maybe<string>;
  expectReply: boolean;
  scheduleAgent: string;
  scheduleSequence: number;
  scheduleUpdated?: Maybe<string>;
  invitedBy?: Maybe<string>;
  locationId?: Maybe<string>;
}

export interface CalendarRecurrenceRule {
  "@type"?: Maybe<string>;
  frequency: string;
  interval: number;
  firstDayOfWeek?: Maybe<string>;
  byDay?: Maybe<{ day: string; nthOfPeriod?: Maybe<number> }[]>;
  byMonthDay?: Maybe<number[]>;
  byMonth?: Maybe<string[]>;
  byYearDay?: Maybe<number[]>;
  byWeekNo?: Maybe<number[]>;
  byHour?: Maybe<number[]>;
  byMinute?: Maybe<number[]>;
  bySetPosition?: Maybe<number[]>;
  count?: Maybe<number>;
  until?: Maybe<string>;
}

// JMAPKit.CalendarEvent (JSCalendar Event, only what the detail view needs is
// typed strictly; everything else stays open).
export interface CalendarEvent {
  id: string;
  baseEventId?: Maybe<string>;
  calendarIds: Record<string, boolean>;
  isDraft: boolean;
  isOrigin: boolean;
  utcStart?: Maybe<string>;
  utcEnd?: Maybe<string>;
  "@type"?: Maybe<string>;
  uid: string;
  title: string;
  description: string;
  descriptionContentType: string;
  created?: Maybe<string>;
  updated?: Maybe<string>;
  sequence: number;
  // JSCalendar LocalDateTime "YYYY-MM-DDTHH:MM:SS" (floating; see timeZone).
  start: string;
  // ISO 8601 duration ("PT1H", "P1D").
  duration?: Maybe<string>;
  timeZone?: Maybe<string>;
  showWithoutTime: boolean;
  status: CalendarEventStatus;
  freeBusyStatus: CalendarFreeBusyStatus;
  privacy: CalendarPrivacy;
  color?: Maybe<string>;
  keywords?: Maybe<Record<string, boolean>>;
  categories?: Maybe<Record<string, boolean>>;
  replyTo?: Maybe<Record<string, string>>;
  participants?: Maybe<Record<string, CalendarParticipant>>;
  recurrenceId?: Maybe<string>;
  recurrenceIdTimeZone?: Maybe<string>;
  recurrenceRule?: Maybe<CalendarRecurrenceRule>;
  recurrenceRules?: Maybe<CalendarRecurrenceRule[]>;
  recurrenceOverrides?: Maybe<Record<string, Record<string, unknown>>>;
  locations?: Maybe<Record<string, CalendarLocation>>;
  mainLocationId?: Maybe<string>;
  virtualLocations?: Maybe<Record<string, CalendarVirtualLocation>>;
  links?: Maybe<Record<string, CalendarLink>>;
  organizerCalendarAddress?: Maybe<string>;
}

// One instance of an event on the calendar. `start`/`end` are ISO 8601
// instants (end exclusive). All-day occurrences encode their nominal days as
// UTC midnights — read them with `allDayRange`, never through the local zone.
export interface CalendarOccurrence {
  // "<eventId>|<recurrenceId or empty>"
  key: string;
  eventId: string;
  calendarId: string;
  calendarIds: string[];
  recurrenceId?: Maybe<string>;
  uid: string;
  title: string;
  start: string;
  end: string;
  isAllDay: boolean;
  timeZone?: Maybe<string>;
  status: CalendarEventStatus;
  freeBusyStatus: CalendarFreeBusyStatus;
  privacy: CalendarPrivacy;
  color?: Maybe<string>;
  locationSummary?: Maybe<string>;
  isRecurring: boolean;
  isDraft: boolean;
  hasParticipants: boolean;
}

export interface CalendarTimedItem {
  key: string;
  // Minutes since local midnight; end exclusive, clipped to the day.
  startMinute: number;
  endMinute: number;
  column: number;
  columnCount: number;
  continuesFromPreviousDay: boolean;
  continuesIntoNextDay: boolean;
}

export interface CalendarSpanItem {
  key: string;
  // Indices into the row's `days`, inclusive.
  startDayIndex: number;
  endDayIndex: number;
  row: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
  // Timed single-day chip in a month cell (draw with its start time).
  isTimed: boolean;
}

export interface CalendarDayColumn {
  date: CalendarDate;
  // 1 = Sunday … 7 = Saturday
  weekday: number;
  isToday: boolean;
  isInAnchorMonth: boolean;
  timedItems: CalendarTimedItem[];
}

export interface CalendarNowIndicator {
  dayIndex: number;
  minute: number;
}

// Day view = 1 column, week view = 7.
export interface CalendarWeekLayout {
  days: CalendarDayColumn[];
  allDayItems: CalendarSpanItem[];
  allDayRowCount: number;
  nowIndicator?: Maybe<CalendarNowIndicator>;
}

export interface CalendarMonthWeek {
  days: CalendarDayColumn[];
  items: CalendarSpanItem[];
  rowCount: number;
}

export interface CalendarMonthLayout {
  weeks: CalendarMonthWeek[];
  weekdayOrder: number[];
}

export interface CalendarAgendaSection {
  date: CalendarDate;
  weekday: number;
  isToday: boolean;
  keys: string[];
}

export interface CalendarAgendaLayout {
  sections: CalendarAgendaSection[];
}

// CalendarCore.CalendarShellSnapshot. Exactly one layout is present,
// matching `viewMode` (`weekLayout` serves both "day" and "week").
export interface CalendarShellSnapshot {
  calendars: JMAPCalendar[];
  // Sorted; empty means every calendar.
  selectedCalendarIds: string[];
  viewMode: CalendarViewMode;
  anchor: CalendarDate;
  today: CalendarDate;
  // IANA (or fixed-offset) zone and week start (1 = Sunday … 7) in effect.
  timeZone: string;
  firstWeekday: number;
  range: CalendarDateRange;
  occurrences: CalendarOccurrence[];
  weekLayout?: Maybe<CalendarWeekLayout>;
  monthLayout?: Maybe<CalendarMonthLayout>;
  agendaLayout?: Maybe<CalendarAgendaLayout>;
  selectedOccurrenceKey?: Maybe<string>;
  selectedEvent?: Maybe<CalendarEvent>;
  isLoading: boolean;
  error?: Maybe<string>;
  lastSyncedAt?: Maybe<string>;
}

export interface MailShellSnapshot {
  accounts: StoredAccount[];
  activeAccountStorageKey?: Maybe<string>;
  mailboxes: Mailbox[];
  selectedMailboxId?: Maybe<string>;
  threadGroups: ThreadGroup[];
  selectedThreadId?: Maybe<string>;
  selectedEmailId?: Maybe<string>;
  // Newest first, filtered to the selected mailbox.
  selectedThreadEmails: EmailMessage[];
  selectedEmail?: Maybe<EmailMessage>;
  canLoadMore: boolean;
  totalEmailCount: number;
  visibleEmailCount: number;
  isRefreshing: boolean;
  error?: Maybe<string>;
  // Web Push registrations of this install (empty when the core has none;
  // tolerated as absent for cores predating the push ops).
  push?: Maybe<PushRegistration[]>;
}

export const EMPTY_SNAPSHOT: MailShellSnapshot = Object.freeze({
  accounts: [],
  mailboxes: [],
  threadGroups: [],
  selectedThreadEmails: [],
  canLoadMore: false,
  totalEmailCount: 0,
  visibleEmailCount: 0,
  isRefreshing: false,
  push: [],
}) as MailShellSnapshot;

// ---------------------------------------------------------------------------
// Shell protocol: lately_shell_open / lately_shell_command / lately_shell_close

// lately_shell_open's config (ShellSession.swift ShellOpenConfigDTO).
export interface ShellOpenConfig {
  serverURL: string;
  username: string;
  // The Basic secret. Optional since shared auth: absent means "use the
  // credential the encrypted store already holds" (SQLiteSecretStore); the
  // open fails with "no stored credential" when there is none.
  password?: string;
  bearerToken?: string;
  // Absent → in-memory SQLite (Node probes). The browser passes the OPFS-backed
  // path so state survives reloads.
  storePath?: string;
  // The vault's store key (base64url or base64, 32+ bytes): the SQLite codec
  // key. Ignored — and reported as `storeEncrypted:false` — when the linked
  // SQLite has no codec.
  storeKey?: string;
  // Test hook (lately-wasm-vault-smoke.mjs): "legacyPassphraseBytes" opens the
  // store the way the pre-KSE-0190 core keyed it. Default "raw".
  storeKeyForm?: "raw" | "legacyPassphraseBytes";
  // Test hook: overrides the reconnect schedule (milliseconds between
  // attempts; the last entry repeats). Default 5 s, 15 s, 30 s, then 60 s.
  reconnectBackoffMs?: number[];
}

// lately_shell_open's completion (ShellSession.swift ShellOpenResultDTO).
export interface ShellOpenResult {
  ok: true;
  accountId?: Maybe<string>;
  // The store is encrypted under the config's storeKey (page codec).
  storeEncrypted: boolean;
  // The credential now rests in the store's `secrets` table (encrypted stores
  // only; a plaintext store never holds one).
  credentialStored: boolean;
  // How the store was opened: plaintext | encrypted | rekeyedInPlace | recreated.
  storeEncryption: string;
}

export interface ShellOkResult {
  ok: true;
}

export interface ShellSendResult {
  ok: true;
  result: EmailSubmissionResult;
}

// MailShellController.downloadAttachment(_:) → JMAPDownloadedBlob: the bytes
// cross the bridge base64-encoded; `name` is the suggested filename.
export interface ShellDownloadedBlob {
  name: string;
  contentType: string | null;
  size: number;
  base64: string;
}

export interface ShellDownloadResult {
  ok: true;
  result: ShellDownloadedBlob;
}

// registerPush → the server-assigned PushSubscription id and the install's
// stable deviceClientId the subscription was created with.
export interface ShellPushRegisterResult {
  ok: true;
  result: { id: string; deviceClientId: string };
}

export interface ShellPushVerifyResult {
  ok: true;
  result: { ok: true };
}

// listPush → PushSubscription/get merged with the persisted verified flag.
export interface ShellPushListResult {
  ok: true;
  result: { subscriptions: PushRegistration[] };
}

// notificationDigest → the reconciler's output for this install.
export interface ShellNotificationDigestResult {
  ok: true;
  result: NotificationDigest;
}

// One-to-one with MailShellController's public methods, plus the account/blob
// ops ShellSession routes to the coordinator:
//   deleteAccount      PersistenceBackedSyncCoordinator.deleteStoredAccount
//                      (disconnects first when it is the active account; the
//                      account's push subscriptions are destroyed and
//                      forgotten on the way)
//   downloadAttachment MailShellController.downloadAttachment(_:)
// and the Web Push ops (PushRegistrationController, RFC 8620 §7.2): the
// browser subscribes with PushManager and hands the endpoint + keys to the
// core, which owns the JMAP PushSubscription and the deviceClientId:
//   registerPush       PushSubscription/set create → {id, deviceClientId};
//                      the server then pushes a PushVerification
//   verifyPush         PushSubscription/set update {verificationCode}
//   unregisterPush     PushSubscription/set destroy + forget
//   listPush           PushSubscription/get merged with the verified flag
export type ShellCommand =
  | { op: "refresh" }
  | { op: "reloadFromPersistence" }
  | { op: "selectMailbox"; mailboxId: string | null }
  | { op: "selectThread"; threadId: string | null }
  | { op: "selectEmail"; emailId: string | null }
  | { op: "loadMore" }
  | { op: "markThreadRead"; threadId: string; read: boolean }
  | { op: "toggleThreadStar"; threadId: string }
  | { op: "deleteThread"; threadId: string }
  | { op: "archiveThread"; threadId: string }
  | { op: "junkThread"; threadId: string }
  | { op: "moveThread"; threadId: string; mailboxId: string }
  | { op: "send"; draft: OutgoingEmailDraft }
  | { op: "deleteAccount"; storageKey: string }
  | { op: "downloadAttachment"; attachment: Attachment }
  | {
      op: "registerPush";
      accountId: string;
      endpoint: string;
      keys: PushSubscriptionKeys;
      // ISO 8601 / JMAP UTCDate; absent lets the server pick its maximum.
      expires?: string;
      // Data types to be notified about; absent → the core's default
      // (Email, Mailbox, Thread).
      types?: string[];
    }
  | { op: "verifyPush"; accountId: string; id: string; verificationCode: string }
  | { op: "unregisterPush"; accountId: string; id: string }
  | { op: "listPush"; accountId: string }
  // The page-side notification digest (MailShellController.notificationDigest):
  // `delivered` is what the browser currently shows (registration
  // .getNotifications() mapped to data.lately + tag); `changed` alone is the
  // StateChange that prompted it (a push the service worker forwarded);
  // `trigger` names the page's own reasons (foreground / stateChange / local)
  // and wins over `changed`. `mayShow` false = the page is hidden and cannot
  // present anything: clears + badge only, new mail is left in the cursor for
  // the service worker's next digest to show (default true).
  | {
      op: "notificationDigest";
      delivered: DeliveredNotification[];
      changed?: Record<string, Record<string, string>> | null;
      trigger?: NotificationDigestTrigger;
      mayShow?: boolean;
    }
  // Calendar shell ops (KSE-0191), one-to-one with CalendarShellController.
  // The core answers through the calendar snapshot stream, not the result:
  //   calendarConfigure     setDisplay(timeZone:firstWeekday:) — the browser's
  //                         IANA zone (Intl.DateTimeFormat().resolvedOptions()
  //                         .timeZone) and week start (1 = Sunday … 7)
  //   calendarSetView       setViewMode / show(_:in:) when `anchor` is given
  //   calendarNavigate      navigate(by:) whole periods (±1 day/week/month/page)
  //   calendarShowToday     showToday()
  //   calendarRefreshClock  refreshClock() — re-derive today + the now
  //                         indicator (the core only recomputes on an op or
  //                         a store change; the page sends this once a
  //                         minute while visible and on foreground)
  //   calendarSelectCalendars selectCalendars(_:) (empty = all)
  //   calendarToggleCalendar toggleCalendar(_:)
  //   calendarSelectOccurrence selectOccurrence(_:) (null clears)
  //   calendarRefresh       refresh() — calendars + the visible window
  | { op: "calendarConfigure"; timeZone: string; firstWeekday: number }
  | { op: "calendarSetView"; viewMode: CalendarViewMode; anchor?: CalendarDate }
  | { op: "calendarNavigate"; delta: number }
  | { op: "calendarShowToday" }
  | { op: "calendarRefreshClock" }
  | { op: "calendarSelectCalendars"; calendarIds: string[] }
  | { op: "calendarToggleCalendar"; calendarId: string }
  | { op: "calendarSelectOccurrence"; key: string | null }
  | { op: "calendarRefresh" };

export type NotificationDigestTrigger =
  | { kind: "push"; changed?: Record<string, Record<string, string>> | null }
  | { kind: "stateChange"; changed?: Record<string, Record<string, string>> | null }
  | { kind: "local"; accountStorageKey: string; emailIds: string[] }
  | { kind: "foreground" };

export type ShellCommandOp = ShellCommand["op"];

export type ShellCommandResult<C extends ShellCommand> = C extends { op: "send" }
  ? ShellSendResult
  : C extends { op: "downloadAttachment" }
    ? ShellDownloadResult
    : C extends { op: "registerPush" }
      ? ShellPushRegisterResult
      : C extends { op: "verifyPush" }
        ? ShellPushVerifyResult
        : C extends { op: "notificationDigest" }
          ? ShellNotificationDigestResult
          : C extends { op: "listPush" }
            ? ShellPushListResult
            : ShellOkResult;

// ---------------------------------------------------------------------------
// Worker RPC (src/core/worker.ts ⇄ src/core/shell.ts)
//
// The worker hosts instantiate() + the OPFS FileBackend; the main thread only
// ever talks to it through these messages.

// RPC method names. They mirror the Promise API instantiate.js exposes
// (shellOpen / shellCommand / shellClose) so a worker that forwards
// `core[name](...args)` needs no translation table. `init` is the worker's
// bootstrap: the main thread hands over the served core URL (from
// `virtual:lately-swift-core`) and the worker fetches instantiate.js, the WASI
// shim and the file backends from that directory — the worker bundle never
// imports the virtual module itself.
export const SHELL_RPC = {
  init: "init",
  open: "shellOpen",
  command: "shellCommand",
  close: "shellClose",
  // Worker-level (not forwarded to the core): `wipe` closes the session and
  // deletes the OPFS directory so no mail remains on disk; `takeover` steals
  // the storage lock from another tab and re-initializes on OPFS.
  wipe: "wipe",
  takeover: "takeover",
} as const;

export type ShellRpcName = (typeof SHELL_RPC)[keyof typeof SHELL_RPC];

export type ShellWorkerStorage = "opfs" | "memory";

// Why the worker is on memory storage: `locked` — another tab holds the OPFS
// pool (Web Lock "lately-fs"); `evicted` — this tab held it and another tab
// took over (the core here is stopped until takeover()); `unsupported` — no
// OPFS in this browser. Absent when on OPFS or when memory was requested.
export type ShellStorageReason = "locked" | "evicted" | "unsupported";

export interface ShellStorageState {
  storage: ShellWorkerStorage;
  reason?: ShellStorageReason;
}

export interface ShellWorkerInitConfig {
  // `coreWasmUrl` from virtual:lately-swift-core (dev: /@fs/…/lately-core.wasm,
  // prod: /lately-swift-core/lately-core.wasm).
  coreWasmUrl: string;
  // Default "opfs" (falls back to memory when OPFS is unavailable or the pool
  // is locked by another tab); "memory" forces the in-memory backend.
  storage?: ShellWorkerStorage;
}

export interface ShellWorkerInitResult extends ShellStorageState {
  abiVersion: number;
}

export type ShellTakeoverResult = ShellStorageState;

export interface ShellCallMessage {
  id: number;
  type: "call";
  name: ShellRpcName;
  args: unknown[];
}

export interface ShellResultMessage {
  id: number;
  type: "result";
  value: unknown;
}

export interface ShellErrorMessage {
  id: number;
  type: "error";
  message: string;
}

export interface ShellSnapshotMessage {
  type: "snapshot";
  value: MailShellSnapshot;
}

// Unsolicited: the core's calendar shell published a new snapshot
// (`host_calendar_snapshot`, KSE-0191). Independent of the mail snapshot so a
// month layout is not re-sent on every mail change.
export interface ShellCalendarSnapshotMessage {
  type: "calendarSnapshot";
  value: CalendarShellSnapshot;
}

// Unsolicited: the worker's storage mode changed (another tab took the pool,
// a takeover succeeded, a wipe re-initialized).
export interface ShellStorageMessage {
  type: "storage";
  value: ShellStorageState;
}

// Optional: a worker may announce that the core is instantiated. ShellClient
// does not require it (calls are queued by the worker until then).
export interface ShellReadyMessage {
  type: "ready";
  engine?: string;
}

export type ShellWorkerInbound = ShellCallMessage;
export type ShellWorkerOutbound =
  | ShellResultMessage
  | ShellErrorMessage
  | ShellSnapshotMessage
  | ShellCalendarSnapshotMessage
  | ShellStorageMessage
  | ShellReadyMessage;
